/* 2FA recovery codes — the client's half.
 *
 * The server issues ten single-use codes, fifteen characters in three groups of five
 * (`xxxxx-xxxxx-xxxxx`), keeps only their hashes, and accepts one at the 2FA step of
 * sign-in in place of the authenticator's six digits. It tells the two apart by SHAPE
 * (server: lib/recoveryCodes.js, looksLikeRecoveryCode): case, spaces and dashes are
 * ignored, and fifteen letters or digits make a recovery code. This mirrors that rule
 * exactly — the shape, not the alphabet the codes are drawn from — so the client can
 * never turn away a code the server would take. A code typed in lower case, with
 * spaces for the dashes or with none at all goes up in the server's own form, and
 * anything that cannot be a code (six digits, a code cut short) is caught before it
 * spends a sign-in attempt.
 *
 * Also here: the Download file, and the "you signed in with a recovery code" notice
 * the portal shows once after such a sign-in (sessionStorage, like the billing prompt's
 * "Not now" in billingPromptDismissal.ts).
 */

export const RECOVERY_CODE_PLACEHOLDER = 'xxxxx-xxxxx-xxxxx';
export const RECOVERY_CODES_FILENAME = 'incrypt-recovery-codes.txt';

// Fifteen letters or digits once the separators are out — the server's test.
const RECOVERY_CODE_SHAPE = /^[A-Z0-9]{15}$/;
// Spaces, and any dash a phone keyboard or a pasted document might put in.
const SEPARATORS = /[-\s‐-―−]+/g;

/** `XXXXX-XXXXX-XXXXX` — or null when the input cannot be a recovery code. */
export const normalizeRecoveryCode = (input: string): string | null => {
  const body = input.replace(SEPARATORS, '').toUpperCase();
  if (!RECOVERY_CODE_SHAPE.test(body)) return null;
  return `${body.slice(0, 5)}-${body.slice(5, 10)}-${body.slice(10)}`;
};

/** The Download file: a header line naming the account and the date, then one code per line. */
export const recoveryCodesFileText = (
  codes: string[],
  email: string,
  when: Date
): string => {
  const day = when.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
  return [`INCRYPT recovery codes for ${email} (generated ${day})`, ...codes]
    .join('\n')
    .concat('\n');
};

/* The sign-in notice. Recorded by the 2FA step when the server answers
 * `usedRecoveryCode`, shown at the top of the portal until the member closes it.
 * AuthContext clears it whenever a session starts or ends, so a later sign-in with the
 * authenticator never inherits it. Storage can be unavailable (private windows, blocked
 * site data): every access is guarded, and the fallback is no notice — the server has
 * emailed them about the code anyway — never a broken sign-in or portal. */
const NOTICE_KEY = 'incrypt_recovery_code_sign_in';

export type RecoveryCodeSignIn = { remaining: number | null };

export const rememberRecoveryCodeSignIn = (remaining: unknown): void => {
  const left =
    typeof remaining === 'number' && Number.isFinite(remaining) ? remaining : null;
  try {
    window.sessionStorage.setItem(NOTICE_KEY, JSON.stringify({ remaining: left }));
  } catch {
    /* no notice this time */
  }
};

export const recoveryCodeSignIn = (): RecoveryCodeSignIn | null => {
  try {
    const stored = window.sessionStorage.getItem(NOTICE_KEY);
    if (!stored) return null;
    const { remaining } = JSON.parse(stored) ?? {};
    return { remaining: typeof remaining === 'number' ? remaining : null };
  } catch {
    return null;
  }
};

export const clearRecoveryCodeSignIn = (): void => {
  try {
    window.sessionStorage.removeItem(NOTICE_KEY);
  } catch {
    /* nothing to clear */
  }
};
